import React, { useEffect, useRef, useState } from "react";

// Scroll-reveal wrapper using IntersectionObserver
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "reveal-visible" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Consistent section heading block
export const SectionHeading = ({ eyebrow, title, sub, align = "center" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} mb-14`}>
    {eyebrow && (
      <Reveal>
        <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-cyan-300">
          {eyebrow}
        </span>
      </Reveal>
    )}
    <Reveal delay={100}>
      <h2 className="font-display mt-5 text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-[2.75rem]">
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={200}>
        <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">{sub}</p>
      </Reveal>
    )}
  </div>
);

// Animated count-up for metrics
export const CountUp = ({ target, suffix = "", duration = 1400 }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");
  const numeric = parseInt(String(target).replace(/[^0-9]/g, ""), 10);

  useEffect(() => {
    const el = ref.current;
    if (!el || isNaN(numeric)) return;
    let raf;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(numeric * eased).toLocaleString());
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [numeric, duration]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};
